import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, MessageCircle, Phone, Contact as ContactIcon, Bell, Heart, Inbox as InboxIcon, ShieldAlert, Check, X, Handshake } from "lucide-react";
import { celebrateMatch } from "@/lib/celebrate";

function Empty({ text }) {
  return <div className="p-10 text-center text-sm text-[color:var(--umg-text-2)]" data-testid="inbox-empty">{text}</div>;
}

function PersonRow({ id, name, photo, sub, right, onClick, testId }) {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      data-testid={testId}
      className="w-full text-left flex items-center gap-3 p-3 border-b transition hover:bg-[color:var(--umg-secondary)]"
      style={{ borderColor: "var(--umg-border)" }}
    >
      <div className="w-10 h-10 rounded-full bg-[color:var(--umg-secondary)] overflow-hidden shrink-0">
        {photo && <img src={photo} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{name}</div>
        {sub && <div className="text-xs text-[color:var(--umg-text-2)] truncate mt-0.5">{sub}</div>}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </Wrapper>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-[#EAE6DF] dark:bg-neutral-800 text-[#1C1917] dark:text-[color:var(--umg-text)]",
    accepted: "bg-[#4F7942] text-white",
    rejected: "bg-black text-white",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest ${styles[status] || styles.pending}`}>{status}</span>;
}

// ---- Sub-tabs ----
function ReceivedTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/connect/incoming").then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try {
      await api.post(`/connect/${id}/${action}`);
      if (action === "accept") {
        celebrateMatch();
        toast.success("🎉 Rishta jud gaya!");
      } else {
        toast.success("Rejected");
      }
      load();
    }
    catch (e) { toast.error(formatError(e)); }
  };
  if (items.length === 0) return <Empty text="No connection requests yet." />;
  return (
    <div>
      {items.map((it) => (
        <PersonRow
          key={it.id}
          testId={`received-row-${it.id}`}
          photo={it.from_photo}
          name={it.from_name}
          sub={it.message ? `"${it.message}"` : new Date(it.created_at).toLocaleString()}
          right={it.status === "pending" ? (
            <div className="flex gap-2">
              <Button data-testid={`accept-${it.id}`} size="sm" onClick={() => act(it.id, "accept")} className="btn-pill bg-[#4F7942] hover:bg-[#3d5f34] text-white h-8 text-xs">
                <Check className="w-3.5 h-3.5 mr-1" /> Accept
              </Button>
              <Button data-testid={`reject-${it.id}`} size="sm" variant="outline" onClick={() => act(it.id, "reject")} className="btn-pill border-[color:var(--umg-border)] h-8 text-xs">
                <X className="w-3.5 h-3.5 mr-1" /> Decline
              </Button>
            </div>
          ) : <StatusBadge status={it.status} />}
        />
      ))}
    </div>
  );
}

function SentTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/connect/sent").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (items.length === 0) return <Empty text="You haven't sent any requests yet." />;
  return items.map((it) => (
    <PersonRow
      key={it.id}
      testId={`sent-row-${it.id}`}
      photo={it.to_photo}
      name={it.to_name}
      sub={new Date(it.created_at).toLocaleString()}
      right={<StatusBadge status={it.status} />}
    />
  ));
}

function AcceptedTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | by-me | by-them
  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([api.get("/connect/incoming"), api.get("/connect/sent")]);
        const accepted = [
          // Received → if I accepted, "Accepted by me"
          ...a.data.filter((x) => x.status === "accepted").map((x) => ({
            id: x.id, kind: "by-me", side: "Accepted by me",
            name: x.from_name, photo: x.from_photo, when: x.created_at, other_id: x.from_id,
          })),
          // Sent → they accepted, "Accepted by them"
          ...b.data.filter((x) => x.status === "accepted").map((x) => ({
            id: x.id, kind: "by-them", side: `Accepted by ${x.to_name}`,
            name: x.to_name, photo: x.to_photo, when: x.created_at, other_id: x.to_id,
          })),
        ].sort((x, y) => (y.when || "").localeCompare(x.when || ""));
        setItems(accepted);
      } catch (e) { toast.error(formatError(e)); }
    })();
  }, [user]);
  const filtered = items.filter((i) => filter === "all" ? true : i.kind === filter);
  const byMe = items.filter((i) => i.kind === "by-me").length;
  const byThem = items.filter((i) => i.kind === "by-them").length;
  return (
    <div>
      <div className="flex items-center gap-2 p-3 border-b" style={{ borderColor: "var(--umg-border)" }}>
        {[
          { key: "all", label: `All (${items.length})` },
          { key: "by-me", label: `Accepted by me (${byMe})` },
          { key: "by-them", label: `Accepted by them (${byThem})` },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`accepted-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs rounded-full border transition ${
              filter === f.key
                ? "bg-[#1C1917] text-white border-[#1C1917]"
                : "border-[color:var(--umg-border)] hover:bg-[color:var(--umg-secondary)]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? <Empty text="No accepted connections in this view." /> :
        filtered.map((it) => (
          <Link key={it.id} to={`/profile/${it.other_id}`} data-testid={`accepted-row-${it.id}`}>
            <PersonRow
              photo={it.photo}
              name={it.name}
              sub={it.side}
              right={<Handshake className={`w-4 h-4 ${it.kind === "by-me" ? "text-[#4F7942]" : "text-[#C85A53]"}`} />}
            />
          </Link>
        ))}
    </div>
  );
}

function SmsTab() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [threads, setThreads] = useState([]);
  useEffect(() => { api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {}); }, []);
  if (threads.length === 0) return <Empty text="No messages yet. Open a profile and tap Message." />;
  return threads.map((th) => (
    <PersonRow
      key={th.user_id}
      testId={`sms-row-${th.user_id}`}
      photo={th.photo}
      name={th.name}
      sub={th.last_text}
      onClick={() => nav(`/messages/${th.user_id}`)}
      right={<span className="text-[10px] text-[color:var(--umg-text-2)]">{new Date(th.last_at).toLocaleDateString()}</span>}
    />
  ));
}

function ContactTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/contact/views").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (items.length === 0) return <Empty text="No one has viewed your contact yet." />;
  return items.map((c, i) => {
    const key = c.id || c.viewer_id || c.at || `contact-${i}`;
    return (
      <PersonRow
        key={key}
        testId={`contact-row-${i}`}
        name={c.viewer_name || "Someone"}
        sub={`Viewed your contact · ${new Date(c.at).toLocaleString()}`}
        right={<ContactIcon className="w-4 h-4 text-[#C85A53]" />}
      />
    );
  });
}

function CallTab() {
  return <Empty text="Call log integration coming soon. Twilio Voice will populate this tab." />;
}

function NotificationsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/notifications").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  if (items.length === 0) return <Empty text="You're all caught up." />;
  return items.map((n, i) => {
    const key = n.id || `${n.kind}-${n.from_id || "?"}-${n.at || i}`;
    return (
      <PersonRow
        key={key}
        testId={`notif-row-${i}`}
        name={n.from_name || "Umaang"}
        sub={n.kind === "like" ? "liked your profile" : n.kind === "emoji" ? `sent ${n.emoji}` : n.kind === "message" ? `📨 ${n.text || ""}` : n.kind}
        right={<span className="text-[10px] text-[color:var(--umg-text-2)]">{new Date(n.at).toLocaleTimeString()}</span>}
        onClick={() => n.from_id && (window.location.href = n.kind === "message" ? `/messages/${n.from_id}` : `/profile/${n.from_id}`)}
      />
    );
  });
}

function MoreTab() {
  const [shortlist, setShortlist] = useState([]);
  const [blocks, setBlocks] = useState({ blocked: [] });
  useEffect(() => {
    api.get("/shortlist").then((r) => setShortlist(r.data)).catch(() => {});
    api.get("/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  }, []);
  const unblock = async (id) => {
    try { await api.delete(`/block/${id}`); toast.success("Unblocked"); api.get("/blocks").then((r) => setBlocks(r.data)); }
    catch (e) { toast.error(formatError(e)); }
  };
  const unshort = async (id) => {
    try { await api.delete(`/shortlist/${id}`); toast.success("Removed"); api.get("/shortlist").then((r) => setShortlist(r.data)); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="p-4 space-y-6">
      <div>
        <h4 className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)] mb-2">Shortlisted ({shortlist.length})</h4>
        {shortlist.length === 0 ? <Empty text="No shortlisted profiles yet." /> :
          shortlist.map((s) => (
            <PersonRow
              key={s.id}
              testId={`shortlist-row-${s.id}`}
              photo={s.photo}
              name={s.name}
              sub={s.city ? `${s.age || ""} · ${s.city}` : `${s.age || ""}`}
              right={
                <div className="flex gap-2">
                  <Link to={`/profile/${s.id}`}><Button size="sm" variant="outline" className="h-8 text-xs">View</Button></Link>
                  <Button data-testid={`unshort-${s.id}`} size="sm" variant="ghost" onClick={() => unshort(s.id)} className="h-8 text-xs">Remove</Button>
                </div>
              }
            />
          ))}
      </div>
      <div>
        <h4 className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)] mb-2">Blocked ({blocks.count || 0})</h4>
        {(!blocks.blocked || blocks.blocked.length === 0) ? <Empty text="No blocked users." /> :
          blocks.blocked.map((id) => (
            <div key={id} className="flex items-center justify-between p-3 border-b" style={{ borderColor: "var(--umg-border)" }}>
              <div className="font-mono text-xs">{id}</div>
              <Button data-testid={`unblock-${id}`} size="sm" variant="outline" onClick={() => unblock(id)} className="h-8 text-xs">Unblock</Button>
            </div>
          ))}
      </div>
    </div>
  );
}

const TABS = [
  { key: "received", label: "Received",     icon: InboxIcon,     comp: ReceivedTab,      color: "#2563EB" },
  { key: "accepted", label: "Accepted",     icon: Handshake,     comp: AcceptedTab,      color: "#059669" },
  { key: "sent",     label: "Sent",         icon: Send,          comp: SentTab,          color: "#0EA5E9" },
  { key: "sms",      label: "SMS",          icon: MessageCircle, comp: SmsTab,           color: "#7C3AED" },
  { key: "call",     label: "Call",         icon: Phone,         comp: CallTab,          color: "#F59E0B" },
  { key: "contact",  label: "Contact",      icon: ContactIcon,   comp: ContactTab,       color: "#DB2777" },
  { key: "notif",    label: "Notification", icon: Bell,          comp: NotificationsTab, color: "#C85A53" },
  { key: "more",     label: "More",         icon: ShieldAlert,   comp: MoreTab,          color: "#6B7280" },
];

export default function Inbox() {
  const [sp, setSp] = useSearchParams();
  const initial = sp.get("tab") && TABS.find((t) => t.key === sp.get("tab")) ? sp.get("tab") : "received";
  const [tab, setTab] = useState(initial);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setSp({ tab }, { replace: true }); }, [tab]);

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <div className="uppercase tracking-[0.28em] text-xs text-[#C85A53] font-semibold">Your inbox</div>
      <h1 className="font-display text-4xl md:text-5xl mt-3">Everything in one place.</h1>

      <Tabs value={tab} onValueChange={setTab} className="mt-8">
        <TabsList className="rounded-full bg-[color:var(--umg-secondary)] p-1 flex-wrap h-auto gap-1">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                data-testid={`inbox-tab-${t.key}`}
                style={active ? { background: t.color, color: "white", boxShadow: `0 4px 12px ${t.color}55` } : {}}
                className="rounded-full text-xs data-[state=active]:!bg-current"
              >
                <t.icon className="w-3.5 h-3.5 mr-1" style={!active ? { color: t.color } : {}} /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <div className="mt-6 border overflow-hidden" style={{ borderRadius: 8, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="m-0">
              <t.comp />
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
}
